import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import type {AuthToken} from '@github-ui/copilot-auth-token/auth-token'
import type {ReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import {tasksApiPath} from '../utils/api-url-helpers'

const ON_DEMAND_SANDBOX_ENVIRONMENT_ID = 'github-sandbox'

interface EnvironmentTaskResponse {
  id?: string
  task?: {id?: string}
  sessions?: Array<{id?: string; environment_id?: string}>
}

interface EnvironmentBoundTaskInput {
  environmentId: string
  problemStatement: string
  copilotApiUrl?: string
  authToken?: AuthToken
  model?: string
  reasoningEffort?: ReasoningEffort
  space?: {owner: string; slug: string}
  origin?: {
    type: string
    identifiers: readonly string[]
    url: string
  }
}

interface RepositoryEnvironmentBoundTaskInput extends EnvironmentBoundTaskInput {
  repositoryNwo: string
  baseRef?: string
  computeScope?: never
}

interface RepositorylessEnvironmentBoundTaskInput extends EnvironmentBoundTaskInput {
  repositoryNwo?: undefined
  baseRef?: never
  computeScope?: string
}

export type CreateEnvironmentBoundTaskInput =
  | RepositoryEnvironmentBoundTaskInput
  | RepositorylessEnvironmentBoundTaskInput

export interface EnvironmentBoundTask {
  taskId: string
  sessionId: string
  boundEnvironmentId?: string
}

export async function createEnvironmentBoundTask(
  input: CreateEnvironmentBoundTaskInput,
  onTaskAllocated?: (taskId: string) => void,
): Promise<EnvironmentBoundTask> {
  const repository = parseNwo(input.repositoryNwo)
  const normalizedInput = normalizeEnvironmentBoundTaskInput(input)
  const response = await fetchCAPI({
    path: tasksApiPath(),
    copilotApiUrl: input.copilotApiUrl,
    authToken: input.authToken,
    method: 'POST',
    additionalHeaders: {Accept: 'application/json'},
    body: {
      environment_id: normalizedInput.environmentId,
      prompt: normalizedInput.problemStatement,
      ...(normalizedInput.model ? {model: normalizedInput.model} : {}),
      ...(normalizedInput.reasoningEffort ? {reasoning_effort: normalizedInput.reasoningEffort} : {}),
      ...(repository ? {repositories: [repository]} : {}),
      ...(repository && normalizedInput.baseRef ? {base_ref: normalizedInput.baseRef} : {}),
      ...(!repository && normalizedInput.computeScope ? {compute: {scope: normalizedInput.computeScope}} : {}),
      ...(normalizedInput.environmentId === ON_DEMAND_SANDBOX_ENVIRONMENT_ID && normalizedInput.space
        ? {space: normalizedInput.space}
        : {}),
      ...(normalizedInput.origin
        ? {
            event_type: normalizedInput.origin.type,
            event_identifiers: [
              ...normalizedInput.origin.identifiers,
              `${normalizedInput.origin.type}_url::${normalizedInput.origin.url}`,
            ],
          }
        : {}),
    },
  })
  const body = (await response.json()) as EnvironmentTaskResponse
  const taskId = body.id ?? body.task?.id
  if (!taskId) throw new Error('Mission Control did not return a task id for this environment task')
  onTaskAllocated?.(taskId)

  const session = body.sessions?.[0]
  if (!session?.id) throw new Error('Mission Control did not create a session for this environment')

  const boundEnvironmentId = session.environment_id
  if (normalizedInput.environmentId === ON_DEMAND_SANDBOX_ENVIRONMENT_ID && !boundEnvironmentId) {
    throw new Error('Mission Control did not bind the cloud session to an environment')
  }

  return {taskId, sessionId: session.id, boundEnvironmentId}
}

function normalizeEnvironmentBoundTaskInput({
  environmentId,
  problemStatement,
  model,
  reasoningEffort,
  repositoryNwo,
  baseRef,
  computeScope,
  space,
  origin,
}: CreateEnvironmentBoundTaskInput): CreateEnvironmentBoundTaskInput {
  if (repositoryNwo) {
    return {
      environmentId,
      problemStatement,
      ...(model ? {model} : {}),
      ...(reasoningEffort ? {reasoningEffort} : {}),
      repositoryNwo,
      ...(baseRef ? {baseRef} : {}),
      ...(space ? {space} : {}),
      ...(origin ? {origin} : {}),
    }
  }

  return {
    environmentId,
    problemStatement,
    ...(model ? {model} : {}),
    ...(reasoningEffort ? {reasoningEffort} : {}),
    ...(computeScope ? {computeScope} : {}),
    ...(space ? {space} : {}),
    ...(origin ? {origin} : {}),
  }
}

function parseNwo(nwo: string | undefined): {owner: string; name: string} | undefined {
  if (nwo === undefined) return undefined
  const [owner, name, ...extraSegments] = nwo.split('/')
  if (!owner || !name || extraSegments.length > 0) {
    throw new Error('Repository must use the owner/name format.')
  }
  return {owner, name}
}
